import { Center, Plane, Svg, Text, useTexture } from "@react-three/drei";
import React, { useState } from "react";
import * as THREE from "three";
import { Map, MapPoint, Scene, TourData } from "../../../../Types";
import {
  UseAssetsQuery,
  UseDownloadAsset,
} from "../../../../api/calls/Queries";
import { useFrame } from "@react-three/fiber";
import { Interactive, useController } from "@react-three/xr";
import { useDataRootStore, useParametersStore } from "../../../../api/Zustand";
import { useCursorShrinkingStore } from "./CursorNew";
import { textMeshMaterial } from "../Popups/Open/XRImagePop";
import { XRNavigateWithLocksCheck } from "../Popups/Closed/XRWaypoint";
import { useGLRendererStore } from "../../../../state management/zustand/gl-stores";
import { useViewerMapStore } from "../../../../state management/zustand/viewer-stores";

let timeout: NodeJS.Timeout;
let timeout2: NodeJS.Timeout;

const XRMap = (props: { scene: Scene; tourData: TourData }) => {
  const { tourID } = useParametersStore();
  const { data: assetList } = UseAssetsQuery(tourID, 2);

  const currentMap = React.useMemo(() => {
    return props.tourData.maps?.find((one) => one.id === props.scene?.map);
  }, [props.tourData, props.scene]);

  const keyToDownload = React.useMemo(() => {
    const thisAsset = assetList?.find(
      (one) => one.assetId === currentMap?.assetId
    );

    return (
      thisAsset?.assetKeyFull ??
      thisAsset?.assetKeyLow ??
      thisAsset?.assetKeyLow
    );
  }, [assetList, currentMap?.assetId]);

  const { data: imageBlob, isLoading } = UseDownloadAsset(keyToDownload);

  const [ratio, setRatio] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    //@ts-ignore
    img.src = null;
    setRatio(undefined);
    if (imageBlob) {
      img.src = imageBlob;
      img.onload = () => {
        setRatio(img.naturalWidth / img.naturalHeight);
      };
    }
  }, [setRatio, imageBlob]);

  return (
    <group position={[0, 5, 0]}>
      {imageBlob && ratio && <TexturePlane blob={imageBlob} ratio={ratio} />}

      {ratio && currentMap && (
        <Points map={currentMap} ratio={ratio} scenes={props.tourData.scenes} />
      )}
    </group>
  );
};

const img = new Image();

const TexturePlane = (props: { blob: string; ratio: number }) => {
  const map = useTexture(props.blob);

  const width: () => number = React.useCallback(() => {
    if (props.ratio >= 1) return 10;
    else return props.ratio * 6;
  }, [props.ratio]);

  const height: () => number = React.useCallback(() => {
    if (props.ratio < 1) return 6;
    else return 10 / props.ratio;
  }, [props.ratio]);

  return (
    <Plane
      renderOrder={3}
      args={[width(), height()]}
      material={
        new THREE.MeshBasicMaterial({
          map: map,
          depthTest: false,
          transparent: true,
        })
      }
    />
  );
};

const Points = (props: { ratio: number; map: Map; scenes: Scene[] }) => {
  const width: number = React.useMemo(() => {
    if (props.ratio! >= 1) return 10;
    else return props.ratio * 6;
  }, [props.ratio]);

  const height: number = React.useMemo(() => {
    if (props.ratio! < 1) return 6;
    else return 10 / props.ratio;
  }, [props.ratio]);

  return (
    <>
      {props.map?.points?.map((point) => (
        <group
          position={[
            width * point.pos.x - width / 2,
            -1 * (height * point.pos.y - height / 2),
            0.25,
          ]}
          key={point.target}
        >
          <Point map={props.map} point={point} scenes={props.scenes} />
        </group>
      ))}
    </>
  );
};

const Point = (props: { map: Map; point: MapPoint; scenes: Scene[] }) => {
  const svgRef = React.useRef<THREE.Group>(null);
  const planeRef = React.useRef<THREE.Mesh>(null);
  const groupRef = React.useRef<THREE.Mesh>(null);
  const textRef = React.useRef<THREE.Mesh>(null);

  const [hover, setHover] = useState<boolean>(false);
  const [textBB, setTextBB] = useState<THREE.Vector3>(
    new THREE.Vector3(1, 1, 1)
  );

  const targetScene = React.useMemo(() => {
    return props.scenes.find((one) => one.id === props.point.target);
  }, [props.scenes, props.point]);

  const cursor = useCursorShrinkingStore();

  const navigateTo = React.useCallback((id: number) => {
    const { state } = useGLRendererStore.getState();

    const dir = new THREE.Vector3();

    const newDir = state?.camera.getWorldDirection(dir).multiplyScalar(-200);

    XRNavigateWithLocksCheck(id, newDir || [0, 0, 200]);
  }, []);

  const viewerMap = useViewerMapStore();

  const gaze = useController("none");

  useFrame((state) => {
    planeRef.current?.lookAt(state.camera.position);

    if (svgRef.current && groupRef.current && textRef.current)
      if (hover) {
        svgRef.current.scale.lerp(new THREE.Vector3(1.2, 1.2, 1.2), 0.1);
        groupRef.current.position.lerp(new THREE.Vector3(0, 0.2, 0.05), 0.1);

        textRef.current.scale.set(1, 1, 1);
      } else {
        svgRef.current.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
        groupRef.current.position.lerp(new THREE.Vector3(0, 0, 0.05), 0.1);
        textRef.current.scale.set(0, 0, 0);
      }
  });

  const { root } = useDataRootStore();

  return (
    <Interactive
      onHover={() => {
        setHover(true);

        clearTimeout(timeout);
        clearTimeout(timeout2);
        cursor.setShrinking(false);

        if (gaze) {
          timeout = setTimeout(() => {
            cursor.setShrinking(true);

            timeout2 = setTimeout(() => {
              viewerMap.setClosed();
              navigateTo(props.point.target);
            }, 2e3);
          }, 1e3);
        }
      }}
      onBlur={() => {
        clearTimeout(timeout);
        clearTimeout(timeout2);
        cursor.setShrinking(false);

        setHover(false);
      }}
      onSelect={() => {
        clearTimeout(timeout);
        clearTimeout(timeout2);
        cursor.setShrinking(false);
        navigateTo(props.point.target);

        viewerMap.setClosed();
        // setCurrentSceneID(props.point.target);
      }}
    >
      {/* <Sphere renderOrder={4} args={[0.2]}>
        <meshBasicMaterial color={"red"} transparent depthTest={false} />
      </Sphere> */}

      {/* <Plane
        ref={planeRef}
        args={[0.6, 0.6]}
        name="hotspot"
        renderOrder={4}
        material={
          new THREE.MeshBasicMaterial({
            visible: true,
            depthTest: false,
            transparent: true,
          })
        }
      >
        {" "}
      </Plane> */}

      <mesh rotation={[Math.PI / 4, 0, 0]} ref={groupRef}>
        <Center ref={svgRef}>
          <group renderOrder={3}>
            <Svg
              renderOrder={1}
              fillMaterial={{
                color: "white",
                transparent: true,
                depthTest: false,
                side: THREE.DoubleSide,
              }}
              src={root + "/svg/xrpin.svg"}
              scale={0.02}
            />
          </group>
        </Center>

        <group position={[0, 0.5, 0]} renderOrder={3}>
          <Text
            ref={textRef}
            textAlign="center"
            overflowWrap="break-word"
            maxWidth={3}
            fontWeight={800}
            fontSize={0.4}
            outlineWidth={0.04}
            outlineColor={"#000"}
            anchorY={"bottom"}
            material={textMeshMaterial}
          >
            {targetScene?.title}
          </Text>
        </group>

        {/* <Text
          renderOrder={4}
          ref={textRef}
          scale={1}
          onSync={(e) => {
            e.position.set(0, e.geometry.boundingBox.max.y + 0.45, 0);
            // setTextBB(e.geometry.boundingBox.max.multiplyScalar(2));
          }}
          fontSize={0.4}
          material={textMeshMaterial}
          textAlign="center"
          overflowWrap="break-word"
          maxWidth={3}
          // anchorY={"bottom"}
        >
          {props.scenes.find((one) => one.id === props.point.target)?.title ||
            "Errore caricamento titolo"}
        </Text> */}
        <Plane
          position={[0, 0, -0.1]}
          material={
            new THREE.MeshBasicMaterial({
              transparent: true,
              opacity: 0.5,
              depthTest: false,
              color: "#2d2e82",
            })
          }
          scale={[textBB.x * 1.1, textBB.y * 1.1, 1]}
        />
      </mesh>
    </Interactive>
  );
};

export default XRMap;
